import '../../App.css';
import './listing.scss';
import { Box, Container, Grid } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TempleCard from './TempleCard';
import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { templesData } from '../../data/templesData.js';
import { doesStateHaveTemples } from '../../data/indiaLocationsData.js';
import SEO from '../../seo/SEO';

export const REGION_STATES_MAP = {
  'north-india': ['Uttar Pradesh', 'Uttarakhand', 'Rajasthan', 'Himachal Pradesh', 'Jammu & Kashmir', 'Jammu and Kashmir', 'Delhi', 'Punjab', 'Haryana', 'Ladakh'],
  'south-india': ['Tamil Nadu', 'Karnataka', 'Kerala', 'Andhra Pradesh', 'Telangana', 'Puducherry'],
  'west-india': ['Gujarat', 'Maharashtra', 'Goa', 'Rajasthan'],
  'east-india': ['Odisha', 'West Bengal', 'Jharkhand', 'Bihar'],
  'central-india': ['Madhya Pradesh', 'Chhattisgarh'],
  'northeast-india': ['Assam', 'Sikkim', 'Tripura', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Arunachal Pradesh'],
};

export const REGION_NAMES_MAP = {
  'north-india': 'North India',
  'south-india': 'South India',
  'west-india': 'West India',
  'east-india': 'East India',
  'central-india': 'Central India',
  'northeast-india': 'Northeast India',
};

const normalizeRegionSlug = (str) =>
  (str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export const normalizeText = (str) => {
  let s = (str || '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (s === 'uttrakhand') return 'uttarakhand';
  return s;
};

export const STATE_CARDS = [
  { id: 'delhi', name: 'Delhi', stateKey: 'Delhi' },
  { id: 'up', name: 'Uttar Pradesh', stateKey: 'Uttar Pradesh' },
  { id: 'uk', name: 'Uttrakhand', stateKey: 'Uttarakhand' },
  { id: 'gujarat', name: 'Gujarat', stateKey: 'Gujarat' },
  { id: 'maharashtra', name: 'Maharashtra', stateKey: 'Maharashtra' },
  { id: 'rajasthan', name: 'Rajasthan', stateKey: 'Rajasthan' },
  { id: 'tamilnadu', name: 'Tamil Nadu', stateKey: 'Tamil Nadu' },
  { id: 'jk', name: 'Jammu & Kashmir', stateKey: 'Jammu & Kashmir' },
  { id: 'hp', name: 'Himachal Pradesh', stateKey: 'Himachal Pradesh' },
  { id: 'ka', name: 'Karnataka', stateKey: 'Karnataka' },
  { id: 'kl', name: 'Kerala', stateKey: 'Kerala' },
  { id: 'ap', name: 'Andhra Pradesh', stateKey: 'Andhra Pradesh' },
  { id: 'mp', name: 'Madhya Pradesh', stateKey: 'Madhya Pradesh' },
  { id: 'od', name: 'Odisha', stateKey: 'Odisha' },
  { id: 'jh', name: 'Jharkhand', stateKey: 'Jharkhand' },
  { id: 'as', name: 'Assam', stateKey: 'Assam' },
];

export default function DetailListing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { regionId } = useParams();

  const initialQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || params.get('q') || '';
  }, [location.search]);

  const [searchTerm, setSearchTerm] = useState(initialQuery);
  const [selectedState, setSelectedState] = useState('');
  const [selectedRegion, setSelectedRegion] = useState(() => {
    if (regionId) return regionId;
    const params = new URLSearchParams(location.search);
    return params.get('region') || '';
  });
  const [showAllStates, setShowAllStates] = useState(false);
  const [visibleCount, setVisibleCount] = useState(20);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('search') || params.get('q');
    const r = regionId || params.get('region');
    const s = params.get('state');

    if (q !== null && q !== undefined) {
      setSearchTerm(q);
      const matched = STATE_CARDS.find(
        (c) =>
          normalizeText(c.name) === normalizeText(q) ||
          normalizeText(c.stateKey) === normalizeText(q)
      );
      if (matched) {
        setSelectedState(matched.stateKey);
      }
    } else {
      setSearchTerm('');
      setSelectedState('');
    }
    if (r) {
      setSelectedRegion(r);
    }
    if (s) {
      setSelectedState(s);
      setSearchTerm(s);
    }

    if ((q && q.trim()) || r || s) {
      setTimeout(() => {
        const section = document.getElementById('all-temples-section');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      }, 150);
    }
  }, [location.search, regionId]);

  // Reset to initial 20 cards when state filter, region filter, or search term changes
  useEffect(() => {
    setVisibleCount(20);
  }, [searchTerm, selectedState, selectedRegion]);

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 20);
  };

  const handleBannerSearch = (e) => {
    if (e) e.preventDefault();
    const q = searchTerm.trim();
    if (q) {
      const matched = STATE_CARDS.find(
        (c) =>
          normalizeText(c.name) === normalizeText(q) ||
          normalizeText(c.stateKey) === normalizeText(q)
      );
      if (matched) {
        setSelectedState(matched.stateKey);
      } else {
        setSelectedState('');
      }
      setSelectedRegion('');
      navigate(`/listing?search=${encodeURIComponent(q)}`, { replace: true });
    } else {
      setSelectedState('');
      setSelectedRegion('');
      navigate('/listing', { replace: true });
    }

    const section = document.getElementById('all-temples-section');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // State metadata
  const stateCards = STATE_CARDS;

  const visibleStates = showAllStates ? stateCards : stateCards.slice(0, 4);

  const handleStateClick = (stateKey) => {
    if (!doesStateHaveTemples(stateKey) || (stateTempleCounts[stateKey] || 0) === 0) {
      navigate('/coming-soon');
      return;
    }

    // Replace the search bar input with the clicked state name (removing previous search name)
    setSearchTerm(stateKey);
    setSelectedState(stateKey);
    setSelectedRegion('');

    // Update URL to match newly selected state
    navigate(`/listing?search=${encodeURIComponent(stateKey)}`, { replace: true });

    // Sync session storage for map highlight persistence
    try {
      sessionStorage.setItem('mw_selected_state', stateKey);
      const saved = sessionStorage.getItem('mw_clicked_states');
      let arr = saved ? JSON.parse(saved) : [];
      if (!Array.isArray(arr)) arr = [];
      const matchExists = arr.some((s) => s.toLowerCase() === stateKey.toLowerCase());
      if (!matchExists) {
        arr.push(stateKey);
        sessionStorage.setItem('mw_clicked_states', JSON.stringify(arr));
      }
    } catch (e) {}

    // Smoothly scroll to temples section
    const section = document.getElementById('all-temples-section');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSeeAllClick = () => {
    if (selectedState) {
      setSelectedState('');
      setSearchTerm('');
      navigate('/listing', { replace: true });
    } else {
      setShowAllStates((prev) => !prev);
    }
  };

  // Priority Static Temples (dedicated static pages matching live data)
  const priorityTemples = useMemo(
    () => [
      {
        name: 'Mata Vaishno Devi Temple',
        location: 'Katra',
        rawLocation: 'Katra, Jammu & Kashmir',
        image: '/assets/trending/vaishno-devi.webp',
        link: '/vaishno-devi',
        historyLink: '/vaishno-devi',
        deity: 'Mata Vaishno Devi',
        slug: 'vaishno-devi',
        state: 'Jammu & Kashmir',
      },
      {
        name: 'Amarnath Cave Temple',
        location: 'Anantnag',
        rawLocation: 'Anantnag, Jammu & Kashmir',
        image: '/assets/tample/amarnath-temple.svg',
        link: '/amarnath',
        historyLink: '/amarnath',
        deity: 'Lord Shiva (Baba Barfani)',
        slug: 'amarnath',
        state: 'Jammu & Kashmir',
      },
      {
        name: 'Dwarkadhish Temple',
        location: 'Dwarka',
        rawLocation: 'Dwarka, Gujarat',
        image: '/assets/trending/dwarkadhish.webp',
        link: '/dwarkadhish-temple',
        historyLink: '/dwarkadhish-temple',
        deity: 'Lord Krishna',
        slug: 'dwarkadhish-temple',
        state: 'Gujarat',
      },
      {
        name: 'Ayodhya Ram Mandir',
        location: 'Ayodhya',
        rawLocation: 'Ayodhya, Uttar Pradesh',
        image: '/assets/trending/ayodhya-mandir.webp',
        link: '/ayodhya',
        historyLink: '/ayodhya',
        deity: 'Lord Ram',
        slug: 'ayodhya',
        state: 'Uttar Pradesh',
      },
      {
        name: 'Kashi Vishwanath Temple',
        location: 'Varanasi',
        rawLocation: 'Varanasi, Uttar Pradesh',
        image: '/assets/trending/kashi-vishwanath.webp',
        link: '/kashi-vishwanath',
        historyLink: '/kashi-vishwanath',
        deity: 'Lord Shiva',
        slug: 'kashi-vishwanath',
        state: 'Uttar Pradesh',
      },
      {
        name: 'Ramanathaswamy Temple',
        location: 'Rameswaram',
        rawLocation: 'Rameswaram, Tamil Nadu',
        image: '/assets/trending/rameshwaram.webp',
        link: '/ramanathaswamy-temple',
        historyLink: '/ramanathaswamy-temple',
        deity: 'Lord Shiva',
        slug: 'ramanathaswamy-temple',
        state: 'Tamil Nadu',
      },
    ],
    []
  );

  // Helper to extract State name from location string
  const getTempleState = (temple) => {
    if (temple.state) return temple.state;
    const loc = ((temple.rawLocation || '') + ' ' + (temple.location || '')).toLowerCase();
    if (loc.includes('delhi')) return 'Delhi';
    if (
      loc.includes('uttar pradesh') ||
      loc.includes('mathura') ||
      loc.includes('ayodhya') ||
      loc.includes('varanasi') ||
      loc.includes('vrindavan') ||
      loc.includes('mirzapur')
    )
      return 'Uttar Pradesh';
    if (
      loc.includes('uttarakhand') ||
      loc.includes('uttrakhand') ||
      loc.includes('haridwar') ||
      loc.includes('kedarnath') ||
      loc.includes('badrinath') ||
      loc.includes('chamoli') ||
      loc.includes('rudraprayag')
    )
      return 'Uttarakhand';
    if (loc.includes('punjab') || loc.includes('amritsar')) return 'Punjab';
    if (loc.includes('gujarat') || loc.includes('dwarka') || loc.includes('somnath') || loc.includes('ambaji') || loc.includes('pavagadh'))
      return 'Gujarat';
    if (loc.includes('rajasthan') || loc.includes('sikar') || loc.includes('jaipur') || loc.includes('churu') || loc.includes('nathdwara'))
      return 'Rajasthan';
    if (loc.includes('maharashtra') || loc.includes('mumbai') || loc.includes('pune') || loc.includes('shirdi') || loc.includes('nashik'))
      return 'Maharashtra';
    if (loc.includes('tamil nadu') || loc.includes('rameswaram') || loc.includes('madurai') || loc.includes('palani') || loc.includes('tiruvannamalai'))
      return 'Tamil Nadu';
    if (loc.includes('kerala') || loc.includes('guruvayur') || loc.includes('thiruvananthapuram') || loc.includes('sabarimala'))
      return 'Kerala';
    if (loc.includes('himachal') || loc.includes('kangra') || loc.includes('bilaspur') || loc.includes('una'))
      return 'Himachal Pradesh';
    if (loc.includes('karnataka') || loc.includes('udupi') || loc.includes('dharmasthala') || loc.includes('subrahmanya'))
      return 'Karnataka';
    if (loc.includes('andhra') || loc.includes('tirupati') || loc.includes('srisailam') || loc.includes('vijayawada'))
      return 'Andhra Pradesh';
    if (loc.includes('madhya pradesh') || loc.includes('ujjain') || loc.includes('omkareshwar') || loc.includes('khandwa'))
      return 'Madhya Pradesh';
    if (loc.includes('odisha') || loc.includes('puri')) return 'Odisha';
    if (loc.includes('assam') || loc.includes('guwahati')) return 'Assam';
    if (loc.includes('jharkhand') || loc.includes('deoghar')) return 'Jharkhand';
    if (loc.includes('jammu') || loc.includes('kashmir') || loc.includes('katra') || loc.includes('anantnag'))
      return 'Jammu & Kashmir';
    return 'Other';
  };

  // All 45 Temples from templesData mapped uniformly
  const datasetTemples = useMemo(() => {
    return templesData.map((t) => ({
      name: t.generalInfo.templeName,
      location: t.generalInfo.location?.split(',')[0]?.trim() || t.generalInfo.location,
      rawLocation: t.generalInfo.location || '',
      image: t.images?.thumbnailImage || '/assets/banner/detail-image.svg',
      link: `/temple/${t.slug}`,
      historyLink: `/temple/${t.slug}`,
      deity: t.generalInfo.deity || '',
      slug: t.slug,
      state: null,
    }));
  }, []);

  // Combined Temples (Priority + Dataset)
  const allTemples = useMemo(() => {
    return [...priorityTemples, ...datasetTemples];
  }, [priorityTemples, datasetTemples]);

  // Dynamically calculate accurate available counts per state
  const stateTempleCounts = useMemo(() => {
    const map = {};
    allTemples.forEach((t) => {
      const s = getTempleState(t);
      map[s] = (map[s] || 0) + 1;
    });
    return map;
  }, [allTemples]);


  // Filter temples by Selected State, Selected Region, and/or Search Term
  const filteredTemples = useMemo(() => {
    let list = allTemples;

    // Filter by Region
    if (selectedRegion) {
      const regSlug = normalizeRegionSlug(selectedRegion);
      const regionStates = REGION_STATES_MAP[regSlug] || [];
      if (regionStates.length > 0) {
        list = list.filter((t) => {
          const state = normalizeText(getTempleState(t));
          const loc = normalizeText(t.rawLocation || t.location || '');
          return regionStates.some((st) => {
            const stNorm = normalizeText(st);
            return state.includes(stNorm) || loc.includes(stNorm);
          });
        });
      }
    }

    // Filter by state
    if (selectedState) {
      const targetNorm = normalizeText(selectedState);
      list = list.filter((t) => {
        const stateNorm = normalizeText(getTempleState(t));
        const rawLocNorm = normalizeText(t.rawLocation || t.location || '');
        return (
          stateNorm.includes(targetNorm) ||
          targetNorm.includes(stateNorm) ||
          rawLocNorm.includes(targetNorm)
        );
      });
    }

    // Filter by banner search term
    if (searchTerm && searchTerm.trim()) {
      const qNorm = normalizeText(searchTerm);
      const isSearchSameAsSelectedState =
        selectedState &&
        (qNorm === normalizeText(selectedState) ||
          STATE_CARDS.some(
            (c) =>
              (normalizeText(c.stateKey) === qNorm || normalizeText(c.name) === qNorm) &&
              normalizeText(c.stateKey) === normalizeText(selectedState)
          ));

      if (!isSearchSameAsSelectedState) {
        const qTokens = qNorm.split(/\s+/).filter(Boolean);

        list = list.filter((t) => {
          const nameNorm = normalizeText(t.name);
          const locNorm = normalizeText(t.location);
          const rawLocNorm = normalizeText(t.rawLocation);
          const deityNorm = normalizeText(t.deity);
          const stateNorm = normalizeText(getTempleState(t));
          const combined = `${nameNorm} ${locNorm} ${rawLocNorm} ${deityNorm} ${stateNorm}`;

          // Direct / inverse match
          if (
            nameNorm.includes(qNorm) ||
            locNorm.includes(qNorm) ||
            rawLocNorm.includes(qNorm) ||
            deityNorm.includes(qNorm) ||
            stateNorm.includes(qNorm) ||
            qNorm.includes(stateNorm) ||
            (qNorm.includes('jammu') && stateNorm.includes('jammu')) ||
            (qNorm.includes('kashmir') && stateNorm.includes('kashmir'))
          ) {
            return true;
          }

          // Multi-token match across all fields
          if (qTokens.length > 1 && qTokens.every((tok) => combined.includes(tok))) {
            return true;
          }

          return false;
        });
      }
    }

    return list;
  }, [selectedRegion, selectedState, searchTerm, allTemples]);

  const displayedRegionName = useMemo(() => {
    if (!selectedRegion) return '';
    const slug = normalizeRegionSlug(selectedRegion);
    return REGION_NAMES_MAP[slug] || selectedRegion.replace(/-/g, ' ');
  }, [selectedRegion]);

  // Paginated temples to display (20 at a time)
  const displayedTemples = useMemo(() => {
    return filteredTemples.slice(0, visibleCount);
  }, [filteredTemples, visibleCount]);

  return (
    <div className="listing-page-wrapper">
      <SEO pageKey="listing" />
      {/* 1. Sacred Mandir Directory Banner with Active Search */}
      <Box className="listing-banner-section">
        <Container maxWidth="md">
          <div className="listing-banner-tag">
            MAPPING SACRED INDIA TOGETHER
          </div>

          <h1 className="listing-banner-title">
            Every mandir in India, in one living directory
          </h1>

          <p className="listing-banner-desc">
            Darshan timings, deities, festivals and travel notes for shrines from the Himalayan passes to the coasts of the south.
          </p>

          <form className="listing-search-form" onSubmit={handleBannerSearch}>
            <SearchIcon className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search a temple"
              value={searchTerm}
              onChange={(e) => {
                const val = e.target.value;
                setSearchTerm(val);
                if (!val.trim()) {
                  setSelectedState('');
                }
              }}
            />
            {searchTerm && (
              <button
                type="button"
                className="search-clear-btn"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedState('');
                  navigate('/listing', { replace: true });
                }}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
            <button type="submit" className="search-button">
              SEARCH
            </button>
          </form>
        </Container>
      </Box>

      {/* 2. Top Pilgrimage Temples in India (All Temples Directory) */}
      <Box id="all-temples-section" className="listing-all-temples-section">
        <Container maxWidth="lg">
          <div className="section-header">
            <div className="header-text">
              <h2>
                {selectedState
                  ? `Temples in ${selectedState} (${filteredTemples.length})`
                  : displayedRegionName
                  ? `Temples in ${displayedRegionName} (${filteredTemples.length})`
                  : searchTerm.trim()
                  ? `Search Results for "${searchTerm}" (${filteredTemples.length})`
                  : `Top Pilgrimage Temples in India (${filteredTemples.length})`}
              </h2>
              <p>
                {selectedState
                  ? `Showing holy shrines and pilgrimage destinations in ${selectedState}`
                  : displayedRegionName
                  ? `Showing holy shrines and sacred pilgrimage destinations across ${displayedRegionName}`
                  : 'Complete pilgrimage guides, darshan timings, aartis, route maps & stays'}
              </p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {selectedRegion && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedRegion('');
                    setSearchTerm('');
                    navigate('/listing', { replace: true });
                  }}
                  style={{
                    backgroundColor: '#00bfa5',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '50px',
                    padding: '6px 16px',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#00a892')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#00bfa5')}
                >
                  <span>Clear region: {displayedRegionName}</span>
                  ✕
                </button>
              )}

              {selectedState && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedState('');
                    setSearchTerm('');
                    navigate('/listing', { replace: true });
                  }}
                  style={{
                    backgroundColor: '#00bfa5',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '50px',
                    padding: '6px 16px',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#00a892')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#00bfa5')}
                >
                  <span>Clear state filter: {selectedState}</span>
                  ✕
                </button>
              )}
            </div>
          </div>

          {filteredTemples.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 16px' }}>
              <p style={{ fontSize: '18px', color: '#666', marginBottom: '16px' }}>
                {selectedState
                  ? `No temples found in ${selectedState} currently.`
                  : `No temples found matching "${searchTerm}".`}
              </p>
              <button
                onClick={() => {
                  setSelectedState('');
                  setSearchTerm('');
                  navigate('/listing');
                }}
                style={{
                  backgroundColor: '#00bfa5',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '50px',
                  padding: '8px 24px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Show All Temples
              </button>
            </div>
          ) : (
            <>
              <Grid container spacing={3}>
                {displayedTemples.map((temple) => (
                  <Grid
                    size={{ xl: 4, lg: 4, md: 6, sm: 12, xs: 12 }}
                    key={temple.slug}
                  >
                    <TempleCard
                      name={temple.name}
                      location={temple.location}
                      image={temple.image}
                      viewLink={temple.link}
                      historyLink={temple.historyLink}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Load More Button */}
              {visibleCount < filteredTemples.length && (
                <div className="load-more-container">
                  <button className="btn-load-more" onClick={handleLoadMore}>
                    <span>Load More</span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  <div className="load-more-count">
                    Showing {displayedTemples.length} of {filteredTemples.length} temples
                  </div>
                </div>
              )}
            </>
          )}
        </Container>
      </Box>

      {/* 3. Where would you like to bow first? - State Filter Section (At the End, seamless with footer) */}
      <Box className="state-filter-section">
        <Container maxWidth="lg">
          <div className="state-section-header">
            <h2 className="state-section-title">
              Where would you like to
              <br />
              bow first?
            </h2>
            <button className="btn-see-all" onClick={handleSeeAllClick}>
              {selectedState ? 'Show All' : showAllStates ? 'Show Less' : 'See All'}
            </button>
          </div>

          <Grid container spacing={2.5}>
            {visibleStates.map((card) => {
              const isSelected =
                Boolean(selectedState && selectedState.toLowerCase() === card.stateKey.toLowerCase()) ||
                Boolean(searchTerm && normalizeText(searchTerm) === normalizeText(card.stateKey));
              const count = stateTempleCounts[card.stateKey] || 0;

              return (
                <Grid size={{ xl: 3, lg: 3, md: 6, sm: 6, xs: 12 }} key={card.id}>
                  <div
                    className={`state-card ${isSelected ? 'active' : ''}`}
                    onClick={() => handleStateClick(card.stateKey)}
                  >
                    <div className="state-icon-circle">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                      </svg>
                    </div>
                    <div className="state-info">
                      <div className="state-name">{card.name}</div>
                      <div className="state-count">
                        {count} {count === 1 ? 'temple' : 'temples'}
                      </div>
                    </div>
                    <div className="state-arrow">→</div>
                  </div>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>
    </div>
  );
}
